import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { LeaderboardEntry } from "../../shared/types";
import { api } from "../lib/network";

type Tab = "global" | "country" | "region";

// Leaderboard playtime is stored in milliseconds (score-per-minute rating uses ms/60000).
function formatPlaytime(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
}

export default function Leaderboard() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [tab, setTab] = useState<Tab>("global");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await api.getLeaderboard(50);
        setEntries(data.entries);
      } catch {
        setEntries([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [tab]);

  const isEmpty = !loading && entries.length === 0;

  return (
    <div className="w-full h-full flex flex-col items-center p-4 sm:p-6 overflow-y-auto">
      <div className="w-full max-w-3xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-gradient-orange uppercase tracking-wider">
              Leaderboard
            </h1>
            <p className="text-vsw-300 text-xs sm:text-sm mt-1">Top rated players worldwide</p>
          </div>
          <button
            onClick={() => navigate("/")}
            className="text-vsw-300 hover:text-vsw-200 text-sm flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {(["global", "country", "region"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg font-display font-bold uppercase text-xs tracking-wider transition-all ${
                tab === t
                  ? "btn-gradient text-white shadow-lg shadow-vsw-500/20"
                  : "bg-board-cell text-vsw-300 hover:text-vsw-200 border border-board-border"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-8 h-8 border-2 border-vsw-400 border-t-transparent rounded-full animate-spin" />
            <div className="text-vsw-300 text-sm">Loading rankings...</div>
          </div>
        ) : isEmpty ? (
          <div className="bg-board-cell rounded-xl border border-board-border p-8 sm:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-vsw-700/30 flex items-center justify-center">
              <svg className="w-8 h-8 text-vsw-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 18L18.5 18M18.5 18L21 20.5M18.5 18L21 15.5M12 21L9.5 21M9.5 21L7 18.5M9.5 21L7 23.5M8 14L6 14M6 14L3.5 11.5M6 14L3.5 16.5M12 11L14.5 11M14.5 11L17 8.5M14.5 11L17 13.5" />
              </svg>
            </div>
            <h3 className="font-display text-lg font-bold text-vsw-200 uppercase tracking-wider mb-2">
              No Rankings Yet
            </h3>
            <p className="text-vsw-300 text-sm max-w-sm mx-auto mb-5">
              Be the first to claim a spot. Play a game and submit your score to appear on the leaderboard.
            </p>
            <button
              onClick={() => navigate("/")}
              className="btn-gradient font-display font-bold uppercase tracking-wider text-white px-6 py-2.5 rounded-lg text-sm hover:scale-105 transition-transform"
            >
              Play Your First Game
            </button>
          </div>
        ) : (
          <div className="bg-board-cell rounded-xl border border-board-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-vsw-300 border-b border-board-border bg-board-bg/50">
                  <th className="px-3 sm:px-4 py-3 text-left font-display text-xs uppercase tracking-wider">Rank</th>
                  <th className="px-3 sm:px-4 py-3 text-left font-display text-xs uppercase tracking-wider">Player</th>
                  <th className="px-3 sm:px-4 py-3 text-right font-display text-xs uppercase tracking-wider">Score</th>
                  <th className="px-3 sm:px-4 py-3 text-right font-display text-xs uppercase tracking-wider">Rating</th>
                  <th className="px-3 sm:px-4 py-3 text-right font-display text-xs uppercase tracking-wider">Time</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, i) => {
                  const rank = i + 1;
                  const isTop3 = rank <= 3;
                  return (
                    <tr
                      key={entry.playerId}
                      className={`border-b border-board-border/50 hover:bg-board-bg/50 transition-colors ${
                        isTop3 ? "bg-vsw-500/5" : ""
                      }`}
                    >
                      <td className="px-3 sm:px-4 py-3">
                        <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-display font-bold ${
                          rank === 1
                            ? "bg-yellow-500/20 text-yellow-400"
                            : rank === 2
                            ? "bg-vsw-300/20 text-vsw-300"
                            : rank === 3
                            ? "bg-orange-500/20 text-orange-400"
                            : "text-vsw-300"
                        }`}>
                          {rank}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-3">
                        <div className="text-vsw-200 font-medium text-sm">{entry.username}</div>
                        <div className="text-vsw-400 text-[10px] uppercase tracking-wider">
                          {entry.country} · {entry.region}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-right text-vsw-200 font-bold font-display">
                        {entry.score.toLocaleString()}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-vsw-700/30 text-vsw-200 text-xs font-display font-bold">
                          {entry.rating.toFixed(1)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-right text-vsw-300 text-xs">
                        {formatPlaytime(entry.playtime)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
